// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * DOM shell wiring for the browser SPA.
 *
 * Responsible for rendering the main shell, toggling panes, updating
 * titles, and delegating PDF lifecycle work to PdfManager.
 */
import type { InitialState, SongbookBook } from "../../shared/src/types";
import { getBookViewId, getEnabledBooks } from "../../shared/src/songbook";
import type { Translator } from "@unilarva/translator";
import type { TranslatorDomBinding } from "@unilarva/translator/dom";
import { renderApp, renderPaneContents } from "../../shared/src/render";
import { parseViewUrl } from "../../shared/src/view-url";
import { PdfManager } from "./pdf-manager";
import type { SearchManager } from "./search-manager";
import type { IndexManager } from "./index-manager";
import type { SongManager } from "./song-manager";
import { SharedControls } from "./shared-controls";
import { hasTouch, prefersReducedMotion } from "./device";

interface BookInfo {
  book: SongbookBook;
  id: number;
  viewId: string;
}

interface WebKitGestureEvent extends Event {
  clientX?: number;
  clientY?: number;
  scale?: number;
}

/**
 * Coordinates the DOM shell (header, panes, body classes) with the
 * application state, translator, and PDF manager.
 */
export class AppShell {
  private readonly state: InitialState;
  private readonly translator: Translator;
  private readonly translationBinding: TranslatorDomBinding;
  private readonly pdfManager: PdfManager;
  private readonly searchManager: SearchManager;
  private readonly indexManager: IndexManager;
  private readonly songManager: SongManager;

  private paneZoom: PaneZoomManager | null = null;

  private sharedControls: SharedControls | null = null;

  private appRoot: HTMLElement | null = null;
  private activeView: string = "home";

  private sideEl: HTMLElement | null = null;

  private swipePointerId: number | null = null;
  private swipeStartX = 0;
  private swipeStartY = 0;

  constructor(
    state: InitialState,
    translator: Translator,
    translationBinding: TranslatorDomBinding,
    pdfManager: PdfManager,
    searchManager: SearchManager,
    indexManager: IndexManager,
    songManager: SongManager,
  ) {
    this.state = state;
    this.translator = translator;
    this.translationBinding = translationBinding;
    this.pdfManager = pdfManager;
    this.searchManager = searchManager;
    this.indexManager = indexManager;
    this.songManager = songManager;
  }

  /**
   * Initializes the DOM shell under the given root, ensuring markup exists
   * and wiring up static UI event handlers.
   */
  init(appRoot: HTMLElement, initialView: string): void {
    this.appRoot = appRoot;
    this.activeView = initialView || "home";

    if (!appRoot.firstElementChild) {
      appRoot.innerHTML = renderApp(this.state, this.translator, this.activeView);
    }

    this.refreshDownloadLabels(appRoot);
    this.translationBinding.update(appRoot);
    this.resetTransientCheckboxState(appRoot);
    this.initImageSpinners(appRoot);
    this.initSharedControls();

    this.paneZoom = new PaneZoomManager(appRoot, () => this.activeView);
    this.paneZoom.init();
    this.paneZoom.preparePane(this.activeView);

    this.sideEl = appRoot.querySelector<HTMLElement>(".sb-side");

    document.addEventListener("click", this.handleMenuToggle);
    appRoot.addEventListener("click", this.handleCardFlip);
    document.addEventListener("click", this.handleOutsideClickToCloseSidePanel, { capture: true });
    window.addEventListener("popstate", this.handlePopStateForSidePanel);

    // Generic dismissibles: modals + dropdown menus.
    document.addEventListener("click", this.handleModalOpenButtons, { capture: true });
    document.addEventListener("click", this.handleModalCloseButtons, { capture: true });
    document.addEventListener("click", this.handleDropdownActionClick, { capture: true });
    document.addEventListener("pointerdown", this.handleOutsidePointerDownForDismissibles, {
      capture: true,
    });
    document.addEventListener("keydown", this.handleEscForDismissibles, { capture: true });
    window.addEventListener("blur", this.handleWindowBlurForDismissibles);

    if (this.sideEl) {
      this.sideEl.addEventListener("pointerdown", this.handleSidePointerDown);
      this.sideEl.addEventListener("pointermove", this.handleSidePointerMove);
      this.sideEl.addEventListener("pointerup", this.handleSidePointerEnd);
      this.sideEl.addEventListener("pointercancel", this.handleSidePointerEnd);
      // Ensure the sidebar is not focusable when closed on initial load.
      this.setSidebarInteractive(false, { keepFocus: true });
    }

    // Initialize accessibility behavior for CSS-driven modals.
    this.initModalAccessibility();

    // Fade in the background image once it has finished loading.
    this.initBackgroundImageFadeIn();
  }

  // Legacy per-pane scroll-to-top removed in favor of SharedControls.

  /**
   * Returns the view currently considered active by the shell.
   */
  getActiveView(): string {
    return this.activeView;
  }

  /**
   * Activates the given view, toggling panes, updating titles and
   * managing PdfjsViewer state via PdfManager.
   */
  showView(view: string): void {
    if (!this.appRoot) throw new Error("AppShell not initialized");

    const previousView = this.activeView;
    let nextView = view || "home";

    if (nextView !== "home") {
      const exists = !!this.appRoot.querySelector(`[data-sb-pane="${this.cssEscape(nextView)}"]`);
      if (!exists) nextView = "home";
    }

    this.activeView = nextView;
    this.state.view = nextView;

    // SSR only includes the requested pane's markup; populate other panes lazily.
    this.ensurePanePopulated(nextView);

    this.appRoot.querySelectorAll<HTMLElement>("[data-sb-pane]").forEach(pane => {
      const paneId = pane.getAttribute("data-sb-pane");
      pane.classList.toggle("active", paneId === nextView);
      pane.classList.toggle("hidden", paneId !== nextView); // Important for SR
    });

    this.updateSideAriaCurrent(nextView);
    this.updateTopbarForView(nextView);

    // Hide side panel and dismiss any transient overlays after navigation.
    if (previousView !== nextView) {
      this.closeSidePanel({ silent: true });
      this.closeAllDismissibles();
    }

    const titleEl = document.getElementById("sb-view-title");
    if (titleEl) titleEl.focus();

    // PDF behavior: only book panes are PDFs (home/search/index are not).
    const bookForNextView =
      nextView === "home" || nextView === "search" || nextView === "index"
        ? null
        : this.findBookByView(nextView);
    const isPdf = !!bookForNextView;

    document.body.classList.toggle("sb-pdf-open", isPdf);

    this.pdfManager.handleViewChange(previousView, nextView);
    this.searchManager.handleViewChange(previousView, nextView);
    this.indexManager.handleViewChange(previousView, nextView);
    this.songManager.handleViewChange(previousView, nextView);
    this.paneZoom?.handleViewChange(previousView, nextView);
  }

  /**
   * Refreshes topbar titles and document.title for the current view.
   * Intended to be called on language changes.
   */
  refreshTitlesForCurrentView(): void {
    this.updateTopbarForView(this.activeView);
    this.songManager.refreshTitle();
    if (this.appRoot) this.refreshDownloadLabels(this.appRoot);
  }

  /** Supplies localized book titles for interpolated download accessible names. */
  private refreshDownloadLabels(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>("[data-sb-download-book-title]").forEach(link => {
      const titleKey = link.dataset.sbDownloadBookTitle;
      if (!titleKey) return;
      this.translationBinding.setValues(link, {
        bookTitle: this.translator.translateKey(titleKey),
      });
    });
  }

  /**
   * Ensures the given pane has content (SSR may have left it empty).
   */
  private ensurePanePopulated(view: string): void {
    if (!this.appRoot) return;

    const pane = this.appRoot.querySelector<HTMLElement>(
      `[data-sb-pane="${this.cssEscape(view)}"]`,
    );
    if (!pane) return;

    // Prefer the explicit marker from SSR / previous populates.
    if (pane.dataset.sbPanePopulated === "1") return;

    // If the pane already has real element children (other than sb-zoom-wrap),
    // mark it populated.
    if (pane.children.length > 0) {
      if (!(pane.children.length === 1 && pane.children[0].classList.contains("sb-zoom-wrap"))) {
        pane.dataset.sbPanePopulated = "1";
        return;
      }
    }

    // A lazy song pane has no SSR result; use the current target, not the initial song ID.
    const songId =
      view === "song" ? (parseViewUrl(new URL(window.location.href)).songId ?? "") : "";
    const html = renderPaneContents(
      this.state,
      this.translator,
      view,
      view === "song" ? { songId, songStatus: songId ? "loading" : "missing-id" } : {},
    );
    if (!html) return;

    pane.innerHTML = html;
    pane.dataset.sbPanePopulated = "1";

    // Make sure translated attributes (title/aria-label/placeholder) are correct.
    this.resetTransientCheckboxState(pane);
    this.refreshDownloadLabels(pane);
    this.translationBinding.update(pane);
    this.initImageSpinners(pane);
    this.registerSharedControls(view, pane);
    this.paneZoom?.preparePane(view);
  }

  private initSharedControls(): void {
    // ViewRouter is created in main.ts; reuse the singleton on window.
    const router = (window as any).__sbViewRouter as import("./router").ViewRouter | undefined;
    const initialView = this.activeView;
    if (!router) return;
    this.sharedControls = new SharedControls({ router, initialView });

    // Register initial panes if present (home/search may already be in DOM).
    const homePane = document.querySelector<HTMLElement>('[data-sb-pane="home"]');
    if (homePane) this.registerSharedControls("home", homePane);
    const searchPane = document.querySelector<HTMLElement>('[data-sb-pane="search"]');
    if (searchPane) this.registerSharedControls("search", searchPane);
    const indexPane = document.querySelector<HTMLElement>('[data-sb-pane="index"]');
    if (indexPane) this.registerSharedControls("index", indexPane);
    const songPane = document.querySelector<HTMLElement>('[data-sb-pane="song"]');
    if (songPane) this.registerSharedControls("song", songPane);
  }

  private registerSharedControls(view: string, pane: HTMLElement): void {
    this.sharedControls?.registerForToTopButton(view, pane);
  }

  /**
   * Updates aria-current="page" on side navigation links based on active view.
   */
  private updateSideAriaCurrent(view: string): void {
    document
      .querySelectorAll<HTMLAnchorElement>(".sb-side nav a[data-sb-view-target]")
      .forEach(a => {
        const target = a.getAttribute("data-sb-view-target") || "";
        if (target === view) a.setAttribute("aria-current", "page");
        else a.removeAttribute("aria-current");
      });
  }

  /**
   * Updates topbar titles and document.title for the active view.
   */
  private updateTopbarForView(view: string): void {
    const titleMainEl = document.querySelector<HTMLElement>(".sb-title-main");
    const titleSubEl = document.querySelector<HTMLElement>(".sb-title-sub");

    const book =
      view === "home" || view === "search" || view === "index" ? null : this.findBookByView(view);

    const mainTitleKey = book
      ? `DYNAMIC.book.${book.id}.title`
      : view === "search"
        ? "search.title"
        : view === "index"
          ? "index.title"
          : view === "song"
            ? "song.title"
            : "site.title.short";

    const subTitleKey = book
      ? `DYNAMIC.book.${book.id}.subtitle`
      : view === "search"
        ? "site.title.short"
        : view === "index"
          ? "site.title.short"
          : view === "song"
            ? "site.title.short"
            : "";

    if (titleMainEl) {
      titleMainEl.setAttribute("data-i18n", mainTitleKey);
      this.translationBinding.updateElement(titleMainEl);
    }

    if (titleSubEl) {
      if (subTitleKey) {
        titleSubEl.setAttribute("data-i18n", subTitleKey);
        this.translationBinding.updateElement(titleSubEl);
      } else {
        titleSubEl.removeAttribute("data-i18n");
        titleSubEl.textContent = "";
      }
    }

    const siteTitle = this.translator.translateKey("site.title");
    if (book) {
      const bt = this.translator.translateKey(`DYNAMIC.book.${book.id}.title`);
      const bs = this.translator.translateKey(`DYNAMIC.book.${book.id}.subtitle`);
      document.title = bs ? `${bt} — ${bs}` : bt;
    } else if (view === "search") {
      const st = this.translator.translateKey("search.title");
      const ss = this.translator.translateKey("search.subtitle");
      const main = ss ? `${st} — ${ss}` : st;
      document.title = siteTitle ? `${main} — ${siteTitle}` : main;
    } else if (view === "index") {
      const it = this.translator.translateKey("index.title");
      const is = this.translator.translateKey("index.subtitle");
      const main = is ? `${it} — ${is}` : it;
      document.title = siteTitle ? `${main} — ${siteTitle}` : main;
    } else {
      document.title = siteTitle;
    }
  }

  /**
   * Hides spinner overlays once their contained <img> elements have
   * finished loading (or failed). Safe to call multiple times.
   */
  private initImageSpinners(root: ParentNode = document): void {
    const spinners = root.querySelectorAll<HTMLElement>(".sb-spinner");

    spinners.forEach(spinner => {
      const images = spinner.querySelectorAll<HTMLImageElement>("img");
      if (images.length === 0) {
        this.markSpinnerDone(spinner);
        return;
      }

      let pending = 0;

      const onDone = () => {
        pending--;
        if (pending <= 0) {
          this.markSpinnerDone(spinner);
        }
      };

      images.forEach(img => {
        if (img.complete) return; // already loaded/errored
        pending++;
        img.addEventListener("load", onDone, { once: true });
        img.addEventListener("error", onDone, { once: true });
      });

      // If all images were already complete
      if (pending === 0) {
        this.markSpinnerDone(spinner);
      }
    });
  }

  /**
   * Marks a spinner as done and, after a short delay, stops its animation
   * to allow the fade-out transition to complete first.
   */
  private markSpinnerDone(spinner: HTMLElement): void {
    if (spinner.classList.contains("sb-spinner-done")) return;

    spinner.classList.add("sb-spinner-done");

    if (prefersReducedMotion()) {
      spinner.classList.add("sb-spinner-idle");
      return;
    }

    const stopDelayMs = 1000;
    window.setTimeout(() => {
      spinner.classList.add("sb-spinner-idle");
    }, stopDelayMs);
  }

  /**
   * Preloads the CSS-defined background image and fades it in once loaded.
   */
  private initBackgroundImageFadeIn(): void {
    const body = document.body;
    if (!body) return;

    // Avoid re-running if we've already processed the background.
    if (body.classList.contains("sb-bg-loaded") || body.classList.contains("sb-bg-noimg")) {
      return;
    }

    const style = getComputedStyle(body);
    const raw = style.getPropertyValue("--sb-bg-image-url").trim();

    if (!raw || raw === "none") {
      body.classList.add("sb-bg-noimg");
      return;
    }

    const match = raw.match(/url\((['"]?)(.*?)\1\)/);
    if (!match || !match[2]) {
      body.classList.add("sb-bg-noimg");
      return;
    }

    const url = match[2];

    const img = new Image();
    const onDone = () => {
      body.classList.add("sb-bg-loaded");
    };

    img.addEventListener("load", onDone, { once: true });
    img.addEventListener("error", onDone, { once: true });
    img.src = url;
  }

  /**
   * Handles topbar / small-screen side-menu toggle button clicks.
   */
  private handleMenuToggle = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const btn = target.closest("[data-sb-menu-toggle]");
    if (!btn) return;

    if (this.isSideOpen()) this.closeSidePanel();
    else this.openSidePanel();
  };

  private isSideOpen(): boolean {
    return document.body.classList.contains("sb-side-open");
  }

  private hasSideHistoryMarker(): boolean {
    const st = window.history.state as unknown;
    return !!st && typeof st === "object" && (st as any).__sbSidePanelOpen === true;
  }

  private pushSideHistoryMarker(): void {
    if (this.hasSideHistoryMarker()) return;
    // Keep URL identical; this entry exists solely so Back closes the panel.
    window.history.pushState({ __sbSidePanelOpen: true }, "", window.location.href);
  }

  private clearSideHistoryMarker(): void {
    if (!this.hasSideHistoryMarker()) return;
    // Router uses `null` state; match that.
    window.history.replaceState(null, "", window.location.href);
  }

  private openSidePanel(opts: { pushHistory?: boolean } = {}): void {
    if (this.isSideOpen()) return;
    document.body.classList.add("sb-side-open");
    this.updateMenuToggleAriaExpanded(true);
    this.setSidebarInteractive(true);
    if (opts.pushHistory !== false) this.pushSideHistoryMarker();
  }

  private closeSidePanel(opts: { silent?: boolean; fromPopState?: boolean } = {}): void {
    // silent: close without calling history.back (used when the app navigates).
    if (opts.fromPopState || opts.silent) {
      document.body.classList.remove("sb-side-open");
      this.updateMenuToggleAriaExpanded(false);
      this.setSidebarInteractive(false, { keepFocus: !!opts.silent });
      if (opts.silent) this.clearSideHistoryMarker();
      return;
    }

    if (this.hasSideHistoryMarker()) {
      // Remove the marker entry so Back doesn't navigate away.
      document.body.classList.remove("sb-side-open");
      this.updateMenuToggleAriaExpanded(false);
      this.setSidebarInteractive(false);
      window.history.back();
      return;
    }

    document.body.classList.remove("sb-side-open");
    this.updateMenuToggleAriaExpanded(false);
    this.setSidebarInteractive(false);
    this.clearSideHistoryMarker();
  }

  /** Handles Back/Forward for the side panel (always overlay). */
  private handlePopStateForSidePanel = (e: PopStateEvent): void => {
    const st = e.state as unknown;
    const wantsOpen = !!st && typeof st === "object" && (st as any).__sbSidePanelOpen === true;

    if (wantsOpen) {
      this.openSidePanel({ pushHistory: false });
    } else if (this.isSideOpen()) {
      this.closeSidePanel({ fromPopState: true });
    }
  };

  /**
   * Closes the side panel when clicking outside of it (overlay/mobile mode).
   * Backdrop clicks are also handled by this.
   */
  private handleOutsideClickToCloseSidePanel = (e: MouseEvent): void => {
    if (!this.isSideOpen()) return;

    const target = e.target as HTMLElement | null;
    if (!target) return;

    // If we are navigating via a sidebar link, ensure we don't leave an extra
    // history entry (created for Back-to-close) behind.
    const navLink = target.closest(".sb-side a[data-view-nav]");
    if (navLink) {
      this.clearSideHistoryMarker();
      // Close immediately for responsiveness; showView() will also close on view change.
      this.closeSidePanel({ silent: true });
      return;
    }

    // Don't close when interacting with the panel itself.
    if (target.closest(".sb-side")) return;

    // Don't fight with the toggle button (let it toggle).
    if (target.closest("[data-sb-menu-toggle]")) return;

    // Clicked outside the sidebar and its toggle while the sidebar is open:
    // close the panel, but prevent the underlying element from also acting
    // on this click (no "click-through" while the dimming overlay is shown).
    e.preventDefault();
    e.stopPropagation();

    this.closeSidePanel();
  };

  /** Swipe left on the side panel to close it (touch only). */
  private handleSidePointerDown = (e: PointerEvent): void => {
    if (!this.isSideOpen()) return;
    if (e.pointerType !== "touch") return;

    this.swipePointerId = e.pointerId;
    this.swipeStartX = e.clientX;
    this.swipeStartY = e.clientY;

    try {
      (e.currentTarget as HTMLElement | null)?.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  private handleSidePointerMove = (e: PointerEvent): void => {
    if (this.swipePointerId !== e.pointerId) return;

    const dx = e.clientX - this.swipeStartX;
    const dy = e.clientY - this.swipeStartY;

    // Close on a decisive left-swipe gesture.
    if (dx < -60 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      this.closeSidePanel();
      this.swipePointerId = null;

      try {
        (e.currentTarget as HTMLElement | null)?.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
  };

  private handleSidePointerEnd = (e: PointerEvent): void => {
    if (this.swipePointerId !== e.pointerId) return;
    this.swipePointerId = null;

    try {
      (e.currentTarget as HTMLElement | null)?.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  /**
   * Updates focusability and aria-hidden state for the sidebar based on whether
   * it is open. When closing, focus is optionally restored to the menu button.
   */
  private setSidebarInteractive(open: boolean, opts: { keepFocus?: boolean } = {}): void {
    if (!this.sideEl) return;
    const sidebar = this.sideEl;
    const menuBtn = document.querySelector<HTMLElement>(".sb-menu-btn[data-sb-menu-toggle]");

    if (open) {
      sidebar.removeAttribute("inert");
      sidebar.setAttribute("aria-hidden", "false");

      // When opening, move focus into the sidebar if it is not already there.
      if (!sidebar.contains(document.activeElement)) {
        const firstFocusable = sidebar.querySelector<HTMLElement>(
          'a, button, [tabindex]:not([tabindex="-1"])',
        );
        if (firstFocusable) {
          try {
            firstFocusable.focus();
          } catch {
            /* ignore */
          }
        }
      }
    } else {
      sidebar.setAttribute("inert", "");
      sidebar.setAttribute("aria-hidden", "true");

      if (
        !opts.keepFocus &&
        menuBtn &&
        document.activeElement &&
        sidebar.contains(document.activeElement)
      ) {
        try {
          (menuBtn as HTMLElement).focus();
        } catch {
          /* ignore */
        }
      }
    }
  }

  /**
   * Synchronizes aria-hidden/inert on a modal container from its checkbox toggle.
   */
  private syncModalStateFromToggle(toggle: HTMLInputElement): void {
    const modal = toggle.nextElementSibling as HTMLElement | null;
    if (!modal || !modal.classList.contains("sb-modal")) return;

    if (toggle.checked) {
      modal.hidden = false;
      modal.removeAttribute("inert");
      modal.setAttribute("aria-hidden", "false");
    } else {
      modal.hidden = true;
      modal.setAttribute("inert", "");
      modal.setAttribute("aria-hidden", "true");
    }
  }

  /**
   * Browser reload/session restore can preserve checkbox state before the SPA
   * boots. These checkboxes are purely transient UI state, so force the stable
   * initial state to prevent opened modals on load/re-render.
   */
  private resetTransientCheckboxState(root: ParentNode = document): void {
    root.querySelectorAll<HTMLInputElement>("input.sb-modal-toggle").forEach(input => {
      input.checked = false;
      input.defaultChecked = false;
      input.setAttribute("autocomplete", "off");
    });
  }

  /** Flips a home card while keeping focus and accessibility on its visible face. */
  private handleCardFlip = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest<HTMLButtonElement>("button.sb-card-flip-btn");
    const card = button?.closest<HTMLElement>(".sb-card--flippable");
    if (!button || !card || !this.appRoot?.contains(card)) return;

    const front = card.querySelector<HTMLElement>(".sb-card-front");
    const back = card.querySelector<HTMLElement>(".sb-card-back");
    if (!front || !back || button.closest("[inert]")) return;

    const flipped = card.classList.toggle("sb-card--flipped");
    const visibleFace = flipped ? back : front;
    const hiddenFace = flipped ? front : back;
    visibleFace.removeAttribute("inert");
    // Transfer focus before making its previous owner inert.
    visibleFace
      .querySelector<HTMLButtonElement>(".sb-card-flip-btn")
      ?.focus({ preventScroll: true });
    hiddenFace.setAttribute("inert", "");
  };

  /**
   * Wires accessibility behavior for CSS-driven modals so that closed modals
   * are not focusable and hidden from assistive technologies.
   */
  private initModalAccessibility(root: ParentNode = document): void {
    const toggles = root.querySelectorAll<HTMLInputElement>("input.sb-modal-toggle");
    toggles.forEach(toggle => {
      // Initialize state for current checked value.
      this.syncModalStateFromToggle(toggle);

      // Keep state in sync when the checkbox is toggled (via label or JS).
      toggle.addEventListener("change", () => this.syncModalStateFromToggle(toggle));
    });
  }

  // ── Dismissibles (modals + dropdown menus) ─────────────────────────────

  private closeAllModals(): boolean {
    let closed = false;
    document.querySelectorAll<HTMLInputElement>("input.sb-modal-toggle:checked").forEach(inp => {
      inp.checked = false;
      this.syncModalStateFromToggle(inp);
      closed = true;
    });
    return closed;
  }

  private closeAllDropdowns(): boolean {
    let closed = false;
    document.querySelectorAll<HTMLDetailsElement>("details.sb-dropdown[open]").forEach(d => {
      d.open = false;
      closed = true;
    });
    return closed;
  }

  private closeAllDismissibles(): void {
    this.closeAllModals();
    this.closeAllDropdowns();
  }

  private openModalByToggleId(toggleId: string): void {
    const id = (toggleId ?? "").trim();
    if (!id) return;

    // Only one overlay open at a time.
    this.closeAllDismissibles();

    const inp = document.getElementById(id) as HTMLInputElement | null;
    if (!inp || inp.type !== "checkbox") return;
    inp.checked = true;
    this.syncModalStateFromToggle(inp);

    // Focus the dialog for accessibility and so Escape is intuitive.
    const dialog = inp.nextElementSibling?.querySelector<HTMLElement>(".sb-modal-dialog");
    if (dialog) {
      setTimeout(() => {
        try {
          dialog.focus();
        } catch {
          /* ignore */
        }
      }, 0);
    }
  }

  private closeModalByToggleId(toggleId: string): void {
    const id = (toggleId ?? "").trim();
    if (!id) return;

    const inp = document.getElementById(id) as HTMLInputElement | null;
    if (!inp || inp.type !== "checkbox" || !inp.classList.contains("sb-modal-toggle")) return;

    inp.checked = false;
    this.syncModalStateFromToggle(inp);
  }

  private handleModalOpenButtons = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const btn = target.closest<HTMLElement>("[data-sb-modal-open]");
    if (!btn) return;

    const toggleId = btn.getAttribute("data-sb-modal-open") || "";
    if (!toggleId) return;

    e.preventDefault();
    e.stopPropagation();
    this.openModalByToggleId(toggleId);
  };

  private handleModalCloseButtons = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const btn = target.closest<HTMLElement>("[data-sb-modal-close]");
    if (!btn) return;

    const toggleId = btn.getAttribute("data-sb-modal-close") || "";
    if (!toggleId) return;

    e.preventDefault();
    e.stopPropagation();
    this.closeModalByToggleId(toggleId);
  };

  private handleOutsidePointerDownForDismissibles = (e: PointerEvent): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    // Dropdown: close when clicking outside; also close after choosing an item.
    const openDropdowns = Array.from(
      document.querySelectorAll<HTMLDetailsElement>("details.sb-dropdown[open]"),
    );
    if (openDropdowns.length > 0) {
      for (const dd of openDropdowns) {
        const clickedInside = dd.contains(target);
        const clickedSummary = !!target.closest("summary");

        if (!clickedInside) {
          dd.open = false;
          continue;
        }

        // Let native summary toggling happen.
        if (clickedSummary) continue;
      }
    }
  };

  private handleEscForDismissibles = (e: KeyboardEvent): void => {
    if (e.key !== "Escape") return;

    // If the sidebar is open, close it first and consume the Escape.
    if (this.isSideOpen()) {
      e.preventDefault();
      e.stopPropagation();
      this.closeSidePanel();
      return;
    }

    // Priority (when sidebar is not open): close modals first, then dropdowns.
    const closedModals = this.closeAllModals();
    const closedDropdowns = this.closeAllDropdowns();

    if (closedModals || closedDropdowns) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  private handleWindowBlurForDismissibles = (): void => {
    // Close modals on window blur (they are true overlays).
    // Keep dropdowns open; losing window focus shouldn't reset the UI.
    this.closeAllModals();
  };

  private handleDropdownActionClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const action = target.closest<HTMLElement>(".sb-dropdown-menu a, .sb-dropdown-menu button");
    if (!action) return;

    const dd = action.closest("details.sb-dropdown") as HTMLDetailsElement | null;
    if (!dd || !dd.open) return;

    // Close after the click has executed (e.g. print handler, download).
    setTimeout(() => {
      dd.open = false;
    }, 0);
  };

  private updateMenuToggleAriaExpanded(open: boolean): void {
    const btn = document.querySelector<HTMLElement>(".sb-menu-btn[data-sb-menu-toggle]");
    if (!btn) return;
    btn.setAttribute("aria-expanded", open ? "true" : "false");
  }

  /** Returns enabled books with their 1-based ids and view ids. */
  private enabledBooks(): BookInfo[] {
    return getEnabledBooks(this.state.books).map((book, idx0) => {
      const id = idx0 + 1;
      return { book, id, viewId: getBookViewId(book, id) };
    });
  }

  /** Resolves the book corresponding to a view id, if any. */
  private findBookByView(view: string): BookInfo | null {
    for (const b of this.enabledBooks()) {
      if (b.viewId === view) return b;
    }
    return null;
  }

  /** Escapes a string for safe use in CSS attribute selectors. */
  private cssEscape(s: string): string {
    const css = (globalThis as any).CSS;
    if (css && typeof css.escape === "function") return css.escape(s);
    return s.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }
}

type ZoomableView = "home" | "search" | "index" | "song";

/**
 * Handles touch pinch zoom and mouse drag/fling scrolling for selected panes.
 */
export class PaneZoomManager {
  private readonly root: HTMLElement;
  private readonly getActiveView: () => string;
  private readonly touchZoomEnabled = hasTouch();
  private readonly allowedViews: Set<ZoomableView> = new Set(["home", "search", "index", "song"]);
  private readonly scales: Record<ZoomableView, number> = { home: 1, search: 1, index: 1, song: 1 };
  private readonly minScale = 1;
  private readonly maxScale = 2.5;
  private initialized = false;

  private zoomLimitHintTarget: HTMLElement | null = null;
  private zoomLimitHintDirection: "in" | "out" | null = null;

  private pinchState: {
    view: ZoomableView;
    pane: HTMLElement;
    wrap: HTMLElement;
    startScale: number;
    startDistance: number;
    paneOrigin: { left: number; top: number } | null;
    pointers: Map<number, { x: number; y: number }>;
  } | null = null;
  private pinchAnimationFrame: number | null = null;
  private gestureZoomActive = false;
  private gestureStartScale = 1;
  private gesturePane: HTMLElement | null = null;
  private gesturePaneOrigin: { left: number; top: number } | null = null;
  private lastGestureCenter: { x: number; y: number } | null = null;
  private pendingPinchUpdate: {
    view: ZoomableView;
    scale: number;
    focus: { x: number; y: number };
    pane: HTMLElement;
    wrap: HTMLElement;
    paneOrigin: { left: number; top: number };
    direction: "out" | "in" | null;
  } | null = null;

  // --- Mouse drag, fling + auto-scroll state ---

  private dragState: {
    pane: HTMLElement;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    lastTime: number;
    lastMovementTime: number;
    velocityX: number;
    velocityY: number;
    active: boolean;
  } | null = null;
  private flingAnimationFrame: number | null = null;
  private flingPreviousTime: number | null = null;
  private static readonly flingDecay = 0.0035;
  private static readonly dragThreshold = 4;
  private autoScrollState: {
    pane: HTMLElement;
    originX: number;
    originY: number;
    offsetX: number;
    offsetY: number;
  } | null = null;
  private autoScrollAnimationFrame: number | null = null;

  constructor(root: HTMLElement, getActiveView: () => string) {
    this.root = root;
    this.getActiveView = getActiveView;
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    if (this.touchZoomEnabled) {
      this.root.addEventListener("pointerdown", this.handlePointerDown, { passive: false });
      this.root.addEventListener("pointermove", this.handlePointerMove, { passive: false });
      this.root.addEventListener("pointerup", this.handlePointerUp, { passive: true });
      this.root.addEventListener("pointercancel", this.handlePointerUp, { passive: true });
      this.root.addEventListener("lostpointercapture", this.handlePointerUp, { passive: true });
      this.root.addEventListener("gesturestart", this.handleGestureStart, { passive: false });
      this.root.addEventListener("gesturechange", this.handleGestureChange, { passive: false });
      document.addEventListener("gestureend", this.handleGestureEnd, { passive: false });
      document.addEventListener("gesturecancel", this.handleGestureEnd, { passive: false });
    }
    this.root.addEventListener("mousedown", this.handleMouseDown);
    this.root.addEventListener("selectstart", this.handleSelectStart);
    window.addEventListener("blur", this.cancelPinch);
    window.addEventListener("pagehide", this.cancelPinch);
    window.addEventListener("resize", this.abortPinchForGeometryChange);
    window.addEventListener("orientationchange", this.abortPinchForGeometryChange);
    window.visualViewport?.addEventListener("resize", this.abortPinchForGeometryChange);
    window.visualViewport?.addEventListener("scroll", this.abortPinchForGeometryChange);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  handleViewChange(previousView: string, nextView: string): void {
    if (previousView !== nextView) {
      this.resetAll();
    }
  }

  preparePane(view: string): void {
    const v = this.normalize(view);
    if (!v) return;
    if (this.touchZoomEnabled) {
      const wrap = this.ensureWrapper(v);
      if (wrap) this.applyScale(v, this.scales[v]);
    }
  }

  private resetAll(): void {
    this.abortPinchForGeometryChange();
    this.cancelPointerMotion();
    this.scales.home = 1;
    this.scales.search = 1;
    this.scales.index = 1;
    this.scales.song = 1;
    for (const v of this.allowedViews) {
      this.applyScale(v, 1);
    }
  }

  private handlePointerDown = (e: PointerEvent): void => {
    if (e.pointerType !== "touch") return;
    if (this.gestureZoomActive) return;

    const view = this.viewFromEvent(e);
    if (!view) return;

    const wrap = this.ensureWrapper(view);
    const pane = this.getPane(view);
    if (!pane || !wrap) return;

    wrap.setPointerCapture?.(e.pointerId);

    if (!this.pinchState || this.pinchState.view !== view) {
      this.pinchState = {
        view,
        pane,
        wrap,
        startScale: this.scales[view],
        startDistance: 0,
        paneOrigin: null,
        pointers: new Map(),
      };
    }

    this.pinchState.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Chrome starts native page pinch-zoom unless the two-touch gesture is
    // explicitly consumed from the beginning. If that happens, it cancels or
    // throttles our pointer stream, making each custom pinch advance only a
    // small amount until the fingers are lifted.
    if (this.pinchState.pointers.size >= 2) {
      const [p1, p2] = Array.from(this.pinchState.pointers.values());
      this.pinchState.startDistance = Math.hypot(p1.x - p2.x, p1.y - p2.y) || 1;
      const paneRect = pane.getBoundingClientRect();
      this.pinchState.paneOrigin = { left: paneRect.left, top: paneRect.top };
      this.pinchState.wrap.style.willChange = "transform";
      pane.dataset.sbPinchActive = "true";
      document.getSelection()?.removeAllRanges();
      e.preventDefault();
    }
  };

  private handlePointerMove = (e: PointerEvent): void => {
    if (this.gestureZoomActive) return;
    if (!this.pinchState) return;
    if (this.pinchState.view !== this.getActiveView()) return;
    if (!this.pinchState.pointers.has(e.pointerId)) return;

    const view = this.pinchState.view;
    const { pane, wrap } = this.pinchState;

    this.pinchState.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.pinchState.pointers.size < 2) return;

    e.preventDefault();

    const [p1, p2] = Array.from(this.pinchState.pointers.values());
    const focus = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
    if (this.pinchState.startDistance === 0) {
      this.pinchState.startDistance = dist || 1;
      return;
    }

    const factor = dist / this.pinchState.startDistance;
    const attempted = this.pinchState.startScale * factor;
    const clamped = this.clamp(attempted);
    const bumpReason = attempted < this.minScale ? "out" : attempted > this.maxScale ? "in" : null;
    const paneOrigin = this.pinchState.paneOrigin;
    if (!paneOrigin) return;
    this.schedulePinchUpdate(view, clamped, focus, pane, wrap, paneOrigin, bumpReason);
  };

  private handlePointerUp = (e: PointerEvent): void => {
    if (!this.pinchState) return;
    this.pinchState.pointers.delete(e.pointerId);
    if (this.pinchState.pointers.size < 2) {
      this.cancelPinch();
    }
  };

  private handleGestureStart = (event: Event): void => {
    const e = event as WebKitGestureEvent;
    const view = this.viewFromEvent(e);
    if (!view) return;
    const pane = this.getPane(view);
    const wrap = this.ensureWrapper(view);
    if (!pane || !wrap) return;

    e.preventDefault();
    this.cancelPinch();
    this.gestureZoomActive = true;
    this.gestureStartScale = this.scales[view];
    this.gesturePane = pane;
    const paneRect = pane.getBoundingClientRect();
    this.gesturePaneOrigin = { left: paneRect.left, top: paneRect.top };
    this.lastGestureCenter = {
      x: typeof e.clientX === "number" ? e.clientX : paneRect.left + paneRect.width / 2,
      y: typeof e.clientY === "number" ? e.clientY : paneRect.top + paneRect.height / 2,
    };
    wrap.style.willChange = "transform";
    pane.dataset.sbPinchActive = "true";
  };

  private handleGestureChange = (event: Event): void => {
    if (!this.gestureZoomActive) return;
    const e = event as WebKitGestureEvent;
    const view = this.normalize(this.getActiveView());
    if (!view) return;
    const pane = this.getPane(view);
    const wrap = this.ensureWrapper(view);
    if (!pane || !wrap) return;

    e.preventDefault();
    if (!this.gesturePaneOrigin || !this.lastGestureCenter) return;
    const focus =
      typeof e.clientX === "number" && typeof e.clientY === "number"
        ? (this.lastGestureCenter = { x: e.clientX, y: e.clientY })
        : this.lastGestureCenter;
    const attempted = this.gestureStartScale * (Number(e.scale) || 1);
    const direction = attempted < this.minScale ? "out" : attempted > this.maxScale ? "in" : null;
    this.schedulePinchUpdate(
      view,
      this.clamp(attempted),
      focus,
      pane,
      wrap,
      this.gesturePaneOrigin,
      direction,
    );
  };

  private handleGestureEnd = (event: Event): void => {
    if (!this.gestureZoomActive) return;
    event.preventDefault();
    this.flushPinchUpdate();
    this.gestureZoomActive = false;
    this.finishPinchVisuals();
  };

  private handleVisibilityChange = (): void => {
    if (document.visibilityState !== "visible") this.cancelPinch();
  };

  private cancelPinch = (): void => {
    this.flushPinchUpdate();
    this.gestureZoomActive = false;
    this.finishPinchVisuals();
    this.pinchState = null;
  };

  private abortPinchForGeometryChange = (): void => {
    if (!this.pinchState && !this.gestureZoomActive) return;
    if (this.pinchAnimationFrame != null) cancelAnimationFrame(this.pinchAnimationFrame);
    this.pinchAnimationFrame = null;
    this.pendingPinchUpdate = null;
    this.gestureZoomActive = false;
    this.finishPinchVisuals();
    this.pinchState = null;
  };

  private finishPinchVisuals(): void {
    this.pinchState?.wrap.style.removeProperty("will-change");
    this.gesturePane
      ?.querySelector<HTMLElement>(":scope > .sb-zoom-wrap")
      ?.style.removeProperty("will-change");
    this.pinchState?.pane.removeAttribute("data-sb-pinch-active");
    this.gesturePane?.removeAttribute("data-sb-pinch-active");
    this.gesturePane = null;
    this.gesturePaneOrigin = null;
    this.lastGestureCenter = null;
    this.hideZoomLimitHint();
  }

  private handleMouseDown = (e: MouseEvent): void => {
    if (e.button !== 0 && e.button !== 1) return;
    const view = this.viewFromEvent(e);
    if (!view) return;
    const pane = this.getPane(view);
    if (!pane) return;

    if (e.button === 1 && this.preservesNativeMiddleClick(e.target)) return;
    if (e.button === 0 && this.isRenderedTextAtPoint(e.clientX, e.clientY, e.target)) return;
    this.cancelPointerMotion();
    if (e.button === 1) {
      this.startAutoScroll(pane, e.clientX, e.clientY);
      e.preventDefault();
      return;
    }
    this.dragState = {
      pane,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      lastTime: performance.now(),
      lastMovementTime: 0,
      velocityX: 0,
      velocityY: 0,
      active: false,
    };
    window.addEventListener("mousemove", this.handleMouseMove, true);
    window.addEventListener("mouseup", this.handleMouseUp, true);
  };

  private handleSelectStart = (e: Event): void => {
    const drag = this.dragState;
    if (!drag) return;
    if (drag.active) {
      e.preventDefault();
      return;
    }
    this.dragState = null;
    this.removeDragListeners();
  };

  private preservesNativeMiddleClick(target: EventTarget | null): boolean {
    return (
      target instanceof Element &&
      !!target.closest(
        'a[href], area[href], button, input, select, textarea, summary, [role="button"], [contenteditable]:not([contenteditable="false"])',
      )
    );
  }

  private isRenderedTextAtPoint(
    clientX: number,
    clientY: number,
    target: EventTarget | null,
  ): boolean {
    if (!(target instanceof Element)) return false;
    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (!node.textContent?.trim()) continue;
      range.selectNodeContents(node);
      for (const rect of range.getClientRects()) {
        if (
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom
        ) {
          range.detach();
          return true;
        }
      }
    }
    range.detach();
    return false;
  }

  private handleMouseMove = (e: MouseEvent): void => {
    const drag = this.dragState;
    if (!drag) return;
    const now = performance.now();
    const dt = now - drag.lastTime || 16;
    const dx = e.clientX - drag.lastX;
    const dy = e.clientY - drag.lastY;
    if (!drag.active) {
      if (
        Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) < PaneZoomManager.dragThreshold
      )
        return;
      drag.active = true;
      drag.pane.style.cursor = "grabbing";
      document.getSelection()?.removeAllRanges();
    }
    e.preventDefault();
    drag.pane.scrollLeft -= dx;
    drag.pane.scrollTop -= dy;
    if (dx !== 0 || dy !== 0) {
      drag.velocityX = (-dx / dt) * 16;
      drag.velocityY = (-dy / dt) * 16;
      drag.lastMovementTime = now;
    }
    drag.lastX = e.clientX;
    drag.lastY = e.clientY;
    drag.lastTime = now;
  };

  private handleMouseUp = (): void => {
    const drag = this.dragState;
    this.removeDragListeners();
    if (!drag) return;
    this.dragState = null;
    drag.pane.style.removeProperty("cursor");
    const recentlyMoved = performance.now() - drag.lastMovementTime < 80;
    if (
      drag.active &&
      recentlyMoved &&
      (Math.abs(drag.velocityX) > 0.5 || Math.abs(drag.velocityY) > 0.5)
    ) {
      this.startFling(drag.pane, drag.velocityX, drag.velocityY);
    }
  };

  private startFling(pane: HTMLElement, velocityX: number, velocityY: number): void {
    this.flingPreviousTime = null;
    const step = (time: number): void => {
      if (this.flingPreviousTime == null) {
        this.flingPreviousTime = time;
        this.flingAnimationFrame = requestAnimationFrame(step);
        return;
      }
      const dt = time - this.flingPreviousTime;
      this.flingPreviousTime = time;
      const decay = Math.exp(-PaneZoomManager.flingDecay * dt);
      velocityX *= decay;
      velocityY *= decay;
      const oldLeft = pane.scrollLeft;
      const oldTop = pane.scrollTop;
      pane.scrollLeft += (velocityX * dt) / 16;
      pane.scrollTop += (velocityY * dt) / 16;
      if (pane.scrollLeft === oldLeft) velocityX = 0;
      if (pane.scrollTop === oldTop) velocityY = 0;

      if (Math.abs(velocityX) > 0.1 || Math.abs(velocityY) > 0.1) {
        this.flingAnimationFrame = requestAnimationFrame(step);
      } else {
        this.flingAnimationFrame = null;
        this.flingPreviousTime = null;
      }
    };
    this.flingAnimationFrame = requestAnimationFrame(step);
  }

  private startAutoScroll(pane: HTMLElement, originX: number, originY: number): void {
    this.autoScrollState = { pane, originX, originY, offsetX: 0, offsetY: 0 };
    pane.style.cursor = "all-scroll";
    window.addEventListener("mousemove", this.handleAutoScrollMove, true);
    window.addEventListener("mouseup", this.handleAutoScrollEnd, true);
    const step = (): void => {
      const state = this.autoScrollState;
      if (!state) {
        this.autoScrollAnimationFrame = null;
        return;
      }
      const deadZone = 8;
      const x =
        Math.abs(state.offsetX) <= deadZone
          ? 0
          : state.offsetX - Math.sign(state.offsetX) * deadZone;
      const y =
        Math.abs(state.offsetY) <= deadZone
          ? 0
          : state.offsetY - Math.sign(state.offsetY) * deadZone;
      state.pane.scrollLeft += x * 0.2;
      state.pane.scrollTop += y * 0.2;
      this.autoScrollAnimationFrame = requestAnimationFrame(step);
    };
    this.autoScrollAnimationFrame = requestAnimationFrame(step);
  }

  private handleAutoScrollMove = (e: MouseEvent): void => {
    const state = this.autoScrollState;
    if (!state) return;
    state.offsetX = e.clientX - state.originX;
    state.offsetY = e.clientY - state.originY;
  };

  private handleAutoScrollEnd = (): void => {
    this.stopAutoScroll();
  };

  private stopAutoScroll(): void {
    this.autoScrollState?.pane.style.removeProperty("cursor");
    this.autoScrollState = null;
    window.removeEventListener("mousemove", this.handleAutoScrollMove, true);
    window.removeEventListener("mouseup", this.handleAutoScrollEnd, true);
    if (this.autoScrollAnimationFrame != null) {
      cancelAnimationFrame(this.autoScrollAnimationFrame);
      this.autoScrollAnimationFrame = null;
    }
  }

  private cancelPointerMotion(): void {
    this.dragState?.pane.style.removeProperty("cursor");
    this.dragState = null;
    this.removeDragListeners();
    if (this.flingAnimationFrame != null) {
      cancelAnimationFrame(this.flingAnimationFrame);
      this.flingAnimationFrame = null;
    }
    this.flingPreviousTime = null;
    this.stopAutoScroll();
  }

  private removeDragListeners(): void {
    window.removeEventListener("mousemove", this.handleMouseMove, true);
    window.removeEventListener("mouseup", this.handleMouseUp, true);
  }

  /** Coalesces high-frequency pointer samples to one visual update per frame. */
  private schedulePinchUpdate(
    view: ZoomableView,
    scale: number,
    focus: { x: number; y: number },
    pane: HTMLElement,
    wrap: HTMLElement,
    paneOrigin: { left: number; top: number },
    direction: "out" | "in" | null,
  ): void {
    this.pendingPinchUpdate = { view, scale, focus, pane, wrap, paneOrigin, direction };
    if (this.pinchAnimationFrame != null) return;
    this.pinchAnimationFrame = requestAnimationFrame(() => {
      this.pinchAnimationFrame = null;
      const update = this.pendingPinchUpdate;
      this.pendingPinchUpdate = null;
      if (update) {
        this.applyScale(update.view, update.scale, {
          focus: update.focus,
          pane: update.pane,
          wrap: update.wrap,
          paneOrigin: update.paneOrigin,
          direction: update.direction,
        });
      }
    });
  }

  /** Applies the final queued sample before ending or interrupting a pinch. */
  private flushPinchUpdate(): void {
    if (this.pinchAnimationFrame != null) {
      cancelAnimationFrame(this.pinchAnimationFrame);
      this.pinchAnimationFrame = null;
    }
    const update = this.pendingPinchUpdate;
    this.pendingPinchUpdate = null;
    if (update) {
      this.applyScale(update.view, update.scale, {
        focus: update.focus,
        pane: update.pane,
        wrap: update.wrap,
        paneOrigin: update.paneOrigin,
        direction: update.direction,
      });
    }
  }

  private applyScale(
    view: ZoomableView,
    scale: number,
    opts: {
      focus?: { x: number; y: number };
      pane?: HTMLElement;
      wrap?: HTMLElement;
      paneOrigin?: { left: number; top: number };
      direction?: "out" | "in" | null;
    } = {},
  ): void {
    const oldScale = this.scales[view];
    const clamped = this.clamp(scale);
    this.scales[view] = clamped;

    const pane = opts.pane ?? this.getPane(view);
    const wrap = opts.wrap ?? this.ensureWrapper(view);
    if (!pane || !wrap) return;

    if (opts.focus) {
      const paneOrigin =
        opts.paneOrigin ??
        (() => {
          const rect = pane.getBoundingClientRect();
          return { left: rect.left, top: rect.top };
        })();
      const offsetX = opts.focus.x - paneOrigin.left;
      const offsetY = opts.focus.y - paneOrigin.top;

      // Convert to unscaled content coords using previous scale, then compute
      // the scroll that keeps that point under the same cursor after scaling.
      const contentX = (pane.scrollLeft + offsetX) / (oldScale || 1);
      const contentY = (pane.scrollTop + offsetY) / (oldScale || 1);

      const nextScrollLeft = contentX * clamped - offsetX;
      const nextScrollTop = contentY * clamped - offsetY;

      pane.scrollLeft = Math.max(0, nextScrollLeft);
      pane.scrollTop = Math.max(0, nextScrollTop);
    }

    if (clamped === 1) {
      wrap.style.removeProperty("--sb-pane-zoom");
      wrap.style.removeProperty("transform");
      wrap.removeAttribute("data-sb-zoom-scale");
    } else {
      wrap.style.setProperty("--sb-pane-zoom", String(clamped));
      wrap.style.setProperty("transform", `scale(${clamped})`);
      wrap.setAttribute("data-sb-zoom-scale", "1");
    }

    if (opts.direction === "out" || opts.direction === "in") {
      this.showZoomLimitHint(opts.direction, pane);
    } else {
      this.hideZoomLimitHint();
    }
  }

  private ensureWrapper(view: ZoomableView): HTMLElement | null {
    const pane = this.getPane(view);
    if (!pane) return null;

    const existing = pane.querySelector<HTMLElement>(":scope > .sb-zoom-wrap");
    if (existing) return existing;

    const wrapper = document.createElement("div");
    wrapper.className = "sb-zoom-wrap";
    wrapper.style.width = "100%";
    wrapper.style.height = "100%";
    wrapper.style.transformOrigin = "0 0";
    wrapper.style.backfaceVisibility = "hidden";
    wrapper.style.setProperty("-webkit-backface-visibility", "hidden");

    // Keep one-finger scrolling native, but reserve two-finger pinch for this
    // manager. Chrome mobile requires touch-action to be known before the
    // gesture starts; preventDefault() on pointermove alone is too late.
    pane.style.touchAction = "pan-x pan-y";
    wrapper.style.touchAction = "pan-x pan-y";

    while (pane.firstChild) {
      wrapper.appendChild(pane.firstChild);
    }
    pane.appendChild(wrapper);
    return wrapper;
  }

  private showZoomLimitHint(direction: "in" | "out", pane: HTMLElement): void {
    let target = pane.querySelector<HTMLElement>(":scope > .sb-pane-zoom-limit-hint");
    if (!target) {
      target = document.createElement("div");
      target.className = "sb-pane-zoom-limit-hint";
      target.setAttribute("aria-hidden", "true");
      pane.appendChild(target);
    }
    if (this.zoomLimitHintTarget === target && this.zoomLimitHintDirection === direction) return;
    const rect = pane.getBoundingClientRect();
    target.style.inset = `${rect.top}px ${window.innerWidth - rect.right}px ${window.innerHeight - rect.bottom}px ${rect.left}px`;
    const baseClass = "sb-pane-zoom-limit-hint";
    const dirClass =
      direction === "in" ? "sb-pane-zoom-limit-hint--in" : "sb-pane-zoom-limit-hint--out";

    this.hideZoomLimitHint();
    this.zoomLimitHintTarget = target;
    this.zoomLimitHintDirection = direction;
    target.classList.add(baseClass, dirClass);
  }

  private hideZoomLimitHint(): void {
    this.zoomLimitHintTarget?.classList.remove(
      "sb-pane-zoom-limit-hint--in",
      "sb-pane-zoom-limit-hint--out",
    );
    this.zoomLimitHintTarget = null;
    this.zoomLimitHintDirection = null;
  }

  private getPane(view: ZoomableView): HTMLElement | null {
    return this.root.querySelector<HTMLElement>(`[data-sb-pane="${this.cssEscape(view)}"]`);
  }

  private viewFromEvent(e: Event): ZoomableView | null {
    const target = e.target as HTMLElement | null;
    if (!target) return null;
    const pane = target.closest<HTMLElement>("[data-sb-pane]");
    if (!pane) return null;
    const view = this.normalize(pane.getAttribute("data-sb-pane") || "");
    if (!view) return null;
    if (view !== this.getActiveView()) return null;
    return view;
  }

  private normalize(view: string): ZoomableView | null {
    return this.allowedViews.has(view as ZoomableView) ? (view as ZoomableView) : null;
  }

  private clamp(val: number): number {
    return Math.min(this.maxScale, Math.max(this.minScale, val));
  }

  private cssEscape(s: string): string {
    const css = (globalThis as any).CSS;
    if (css && typeof css.escape === "function") return css.escape(s);
    return s.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }
}
